// @ts-ignore
import FaviconError from '@assets/icons/favicon-eror.ico'
// @ts-ignore
import Favicon from '@assets/icons/favicon.ico'

const ItemType = {
  Session: 1,
  Storage: 2
};

export class URLLockManager {
    private localStorageTimeout = 15000 * 1000;
    private storagePrefix = 'url_lock_';
    sessionGuid: string | null
    currentUrl: string | null
    private pendingResolvers: { [url: string]: () => void } = {};
  
    private constructor() {
      this.sessionGuid = this.createGUID();

      window.addEventListener('beforeunload', (event) => {
        let stored = this.getItem(ItemType.Storage, this.currentUrl)
        if (this.sessionGuid === stored?.guid) {  
          this.removeItem(ItemType.Storage, this.currentUrl)
        }
      });

      window.addEventListener('storage', (event) => {
        if (event.key !== 'tab-broadcast') {
          return
        }
        const data = JSON.parse(event.newValue);
        if (!data) {
          return;
        }

        if (data.action === 'relinquish-control') {
          if (this.currentUrl == data.url) {
            const isOtherSession = data.sessionGuid !== this.sessionGuid;
            this.updateFavicon(isOtherSession)
            if (isOtherSession) {
              window.dispatchEvent(new CustomEvent('url-lock:relinquished'))
            }
          }
          return;
        }

        if (data.action === 'request-save') {
          if (this.currentUrl == data.url && data.sessionGuid !== this.sessionGuid) {
            window.dispatchEvent(new CustomEvent('url-lock:save-request', { detail: { sessionGuid: data.sessionGuid, url: data.url } }))
          }
          return;
        }

        if (data.action === 'project-saved') {
          if (data.targetSessionGuid === this.sessionGuid) {
            const resolver = this.pendingResolvers[data.url];
            resolver && resolver();
            delete this.pendingResolvers[data.url];
          }
        }
      });
    }

    updateFavicon(isError: boolean) {
      if (isError) {
        document.getElementById('favicon').setAttribute('href', FaviconError)
      } else {
        document.getElementById('favicon').setAttribute('href', Favicon)
      }
    }

    private static instance: URLLockManager

    static getInstance(): URLLockManager {
      if (!this.instance) {
        this.instance = new URLLockManager()
      }
      return this.instance
    }
    
    clear() {
      if (this.currentUrl && this.sessionGuid === this.getItem(ItemType.Storage, this.currentUrl)?.guid){
        this.removeItem(ItemType.Storage, this.currentUrl)
      }
      this.currentUrl = null;
    }

    private getStorageKey(name: string): string{
      return `${this.storagePrefix}${encodeURIComponent(name)}`;
    }

    // New method to handle URL changes
    handleURLChange(url: string) {
      if (!this.currentUrl) {
        this.updateFavicon(false);
        this.currentUrl = url;
        return;
      }

      let stored = this.getItem(ItemType.Storage, url)
      if (!stored) { // no stored lock, meaning we are no longer viewing a duplicate tab
        this.updateFavicon(false);
      } else if (this.sessionGuid === stored.guid){
        this.removeItem(ItemType.Storage, this.currentUrl)
      }
      this.currentUrl = url
    }

    takeOverTab(url: string) {
      this.setItem(ItemType.Session, url, this.sessionGuid);

      var newTabObj = {
        guid: this.sessionGuid,
        timestamp: new Date().getTime(),
      };
      this.setItem(ItemType.Storage, url, JSON.stringify(newTabObj));

      this.broadcast('relinquish-control', { sessionGuid: this.sessionGuid, url });
      this.updateFavicon(false)
    }

    requestTakeover(url: string): Promise<void> {
      return Promise.race([
        new Promise<void>((resolve) => {
          this.pendingResolvers[url] = () => { resolve(void 0); };
          this.broadcast('request-save', { sessionGuid: this.sessionGuid, url });
        }),
        new Promise<void>((resolve) => {
          setTimeout(() => resolve(void 0), 300);
        })
      ]).then(() => {
        this.takeOverTab(url);
      });
    }

    respondProjectSaved(targetSessionGuid: string, url: string) {
      this.broadcast('project-saved', { targetSessionGuid, url });
    }

    private broadcast(action: string, payload: any) {
      window.localStorage.setItem('tab-broadcast', JSON.stringify({
        action,
        ...payload,
      }));
      setTimeout(() => {
        window.localStorage.removeItem('tab-broadcast');
      }, 500);
    }
  
    isTabDuplicated(url: string): boolean {
      var tabObj = this.getItem(ItemType.Storage, url);
  
      //console.log("In testTab");
      let sessionGuid = this.getItem(ItemType.Session, url)?.guid || this.sessionGuid;
      this.setItem(ItemType.Session, url, this.sessionGuid);
        
      // If no or stale tab object, our session is the winner.  If the guid matches, ours is still the winner
      if (
        tabObj === null ||
        tabObj.timestamp < new Date().getTime() - this.localStorageTimeout ||
        tabObj.guid === sessionGuid
      ) {
       
        var newTabObj = {
          guid: sessionGuid,
          timestamp: new Date().getTime()
        };
        this.setItem(ItemType.Storage, url, JSON.stringify(newTabObj));
        
        return false;
      } else {
        return true;
      }
    }

    private createGUID() {
      const s4 = function() {
        return Math.floor((1 + Math.random()) * 0x10000)
          .toString(16)
          .substring(1);
      };
      return (
        s4() +
        s4() +
        "-" +
        s4() +
        "-" +
        s4() +
        "-" +
        s4() +
        "-" +
        s4() +
        s4() +
        s4()
      );
    }

    private getItem(itemtype, url): any {
      var val = "";
      switch (itemtype) {
        case ItemType.Session:
          val = window.name;
          break;
        case ItemType.Storage:
          try {
            val = localStorage.getItem(this.getStorageKey(url));
            if (val && val.trim() !== '') {
              try {
                val = JSON.parse(val);
              } catch (parseError) {
                // Corrupted data, clear it
                console.warn('Corrupted URL lock data detected, clearing:', parseError);
                localStorage.removeItem(this.getStorageKey(url));
                val = null;
              }
            } else {
              val = null;
            }
          } catch (e) {
            console.error('Failed to get URL lock from storage:', e);
            val = null;
          }
          break;
      }
      return val;
    }
  
    private setItem(itemtype, url, val): any {
      switch (itemtype) {
        case ItemType.Session:
          window.name = val;
          break;
        case ItemType.Storage:
          try {
            localStorage.setItem(this.getStorageKey(url), val);
          } catch (e) {
            // Handle quota exceeded errors
            if (e instanceof DOMException && e.name === 'QuotaExceededError') {
              console.error('localStorage quota exceeded when setting URL lock');
              // Try to clean up old locks
              try {
                const keys = Object.keys(localStorage);
                const lockKeys = keys.filter(k => k.startsWith(this.storagePrefix));
                // Remove oldest locks if quota exceeded
                if (lockKeys.length > 10) {
                  const lockEntries = lockKeys.map(key => {
                    try {
                      const data = localStorage.getItem(key);
                      if (data) {
                        const parsed = JSON.parse(data);
                        return { key, timestamp: parsed?.timestamp || 0 };
                      }
                    } catch (parseError) {
                      // Corrupted data, prioritize for removal
                      return { key, timestamp: 0 };
                    }
                    return { key, timestamp: 0 };
                  });
                  
                  // Sort by timestamp (oldest first)
                  lockEntries.sort((a, b) => a.timestamp - b.timestamp);
                  
                  // Remove oldest 5 locks
                  lockEntries.slice(0, 5).forEach(entry => {
                    try {
                      localStorage.removeItem(entry.key);
                    } catch (removeError) {
                      // Ignore individual remove errors
                    }
                  });
                  
                  // Retry setting the current lock
                  try {
                    localStorage.setItem(this.getStorageKey(url), val);
                  } catch (retryError) {
                    console.error('Failed to set URL lock after cleanup:', retryError);
                  }
                }
              } catch (cleanupError) {
                console.error('Failed to cleanup old locks:', cleanupError);
              }
            } else {
              console.error('Failed to set URL lock in storage:', e);
            }
          }
          break;
      }
    }

    private removeItem(itemtype, url): void {
      switch (itemtype) {
        case ItemType.Session:
          window.name = '';
          break;
        case ItemType.Storage:
          try {
            localStorage.removeItem(this.getStorageKey(url));
          } catch (e) {
            console.error('Failed to remove URL lock from storage:', e);
          }
          break;
      }
    } 
  }